/**
 * Strategy Alignment Page
 * 
 * Makes C3.A3 (Leaders audit alignment by inspecting mappings) true.
 * Provides a visual interface for viewing and managing strategy-priority alignment.
 */

import { useState, useEffect } from 'react';
import {
  Target,
  Link as LinkIcon,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  TrendingUp,
  Clock,
  Layers,
  BarChart3,
  Lightbulb,
  GitBranch,
} from 'lucide-react';
import { PageHeader } from '../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './prioritization/strategy/StrategyConnectivityDocModal';
import { Link } from 'react-router-dom';
import {
  generateAlignmentReport,
  getObjectiveTree,
  type AlignmentReport,
  type AlignmentGap,
  type ObjectiveNode,
} from '../lib/strategyAlignment';
import { createStrategicObjective as createObjective } from '../lib/strategy/createStrategyFlow';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { useFeatureFlag, FEATURE_FLAGS } from '../lib/featureFlags';
import {
  getWorkspaceModules,
  isModuleEnabled,
  WORKSPACE_MODULES_UPDATED_EVENT,
} from '../lib/workspaceModulesService';
import { useUserStore } from '../stores/prioritization/userStore';
import { useItemsStore } from '../stores/prioritization/itemsStore';

// ============================================================================
// Subcomponents
// ============================================================================

function AlignmentScoreRing({ score }: { score: number }) {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = () => {
    if (score >= 80) return 'text-[var(--green)] dark:text-[var(--text-ter)]';
    if (score >= 60) return 'text-[var(--mustard)]';
    return 'text-[var(--coral)]';
  };
  
  return (
    <div className="relative w-32 h-32">
      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-[var(--text-ter)]"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={getColor()}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-2xl font-bold ${getColor()}`}>{score}%</span>
      </div>
    </div>
  );
}

function GapCard({ gap }: { gap: AlignmentGap }) {
  const severityConfig = {
    high: { bg: 'bg-[var(--coral-dim)]', border: 'border-[var(--coral)]', text: 'text-[var(--coral)]', icon: 'text-[var(--coral)]' },
    medium: { bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', icon: 'text-[var(--mustard)]' },
    low: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', icon: 'text-[var(--text-ter)]' },
  };
  
  const config = severityConfig[gap.severity];
  
  return (
    <div className={`${config.bg} ${config.border} border  p-4`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${config.icon}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
              {gap.severity.toUpperCase()}
            </span>
            <span className="text-xs text-[var(--text-ter)] capitalize">
              {gap.type.replace(/_/g, ' ')}
            </span>
          </div>
          <p className={`mt-1 text-sm font-medium ${config.text}`}>{gap.description}</p>
          <p className="mt-2 text-sm text-[var(--text-sec)]">{gap.recommendation}</p>
        </div>
      </div>
    </div>
  );
}

function ObjectiveTreeNode({ node, level = 0 }: { node: ObjectiveNode; level?: number }) {
  const [expanded, setExpanded] = useState(level < 2);
  const hasChildren = node.children.length > 0;
  
  const priorityColors = {
    critical: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    high: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    medium: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    low: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  };
  
  const timeHorizonIcons = {
    quarterly: Clock,
    annual: TrendingUp,
    'multi-year': Layers,
  };
  
  const TimeIcon = timeHorizonIcons[node.objective.timeHorizon];
  
  return (
    <div style={{ marginLeft: `${level * 24}px` }}>
      <div className="flex items-center gap-2 py-2 border-b border-[var(--rule)]">
        {hasChildren ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-[var(--hover-tint)] rounded"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
            ) : (
              <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
            )}
          </button>
        ) : (
          <div className="w-6" />
        )}
        
        <Target className="h-4 w-4 text-consulting-slate-blue-500" />
        
        <span className="font-medium text-[var(--text-pri)] flex-1">{node.objective.name}</span>
        
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[node.objective.priority]}`}>
            {node.objective.priority}
          </span>
          
          <div className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <TimeIcon className="h-3 w-3" />
            {node.objective.timeHorizon}
          </div>
          
          {node.mappedPriorities.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
              <LinkIcon className="h-3 w-3" />
              {node.mappedPriorities.length} linked
            </div>
          )}
        </div>
      </div>
      
      {expanded && node.children.map(child => (
        <ObjectiveTreeNode key={child.objective.id} node={child} level={level + 1} />
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/** Read non-archived items from store at call time (snapshot, not reactive). */
function getActiveItemArgs(): { priorityIds: string[]; priorityNames: Map<string, string> } {
  const items = useItemsStore.getState().items.filter(
    (i) => (i as { exit_status?: string }).exit_status !== 'archived',
  );
  return {
    priorityIds: items.map((i) => i.id),
    priorityNames: new Map(items.map((i) => [i.id, i.title])),
  };
}

export default function StrategyAlignmentPage() {
  const userId = useUserStore((state) => state.userId);
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  const kernelFlagEnabled = useFeatureFlag(FEATURE_FLAGS.STRATEGY_KERNEL_NAV);
  const [workspaceFeatures, setWorkspaceFeatures] = useState<Record<string, boolean>>({});
  const kernelEnabled = kernelFlagEnabled && isModuleEnabled(workspaceFeatures, 'strategy_kernel');

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (!currentWorkspaceId) { if (!cancelled) setWorkspaceFeatures({}); return; }
      getWorkspaceModules(currentWorkspaceId).then(({ data }) => {
        if (!cancelled) setWorkspaceFeatures(data);
      });
    };
    load();
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    return () => { cancelled = true; window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load); };
  }, [currentWorkspaceId]);
  const itemCount = useItemsStore((s) => s.items.length);
  
  const [report, setReport] = useState<AlignmentReport | null>(null);
  const [objectiveTree, setObjectiveTree] = useState<ObjectiveNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'objectives' | 'gaps'>('overview');
  
  // Add Objective Modal state
  const [showAddObjectiveModal, setShowAddObjectiveModal] = useState(false);
  const [newObjectiveName, setNewObjectiveName] = useState('');
  const [newObjectiveDescription, setNewObjectiveDescription] = useState('');
  const [newObjectiveTimeHorizon, setNewObjectiveTimeHorizon] = useState<'quarterly' | 'annual' | 'multi-year'>('quarterly');
  const [newObjectivePriority, setNewObjectivePriority] = useState<'critical' | 'high' | 'medium' | 'low'>('high');
  
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!userId || !currentWorkspaceId) {
        setLoading(false);
        setLoadError(null);
        setReport(null);
        setObjectiveTree([]);
        return;
      }

      setLoading(true);
      setLoadError(null);
      const LOAD_TIMEOUT_MS = 12_000;

      try {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Load timed out')), LOAD_TIMEOUT_MS)
        );

        const work = async () => {
          // Ensure items are loaded before building the report.
          if (useItemsStore.getState().items.length === 0) {
            await fetchItems();
          }
          const { priorityIds, priorityNames } = getActiveItemArgs();
          return Promise.all([
            generateAlignmentReport(currentWorkspaceId, priorityIds, priorityNames),
            getObjectiveTree(currentWorkspaceId),
          ]);
        };

        const [alignmentReport, tree] = await Promise.race([work(), timeoutPromise]);
        setReport(alignmentReport);
        setObjectiveTree(tree);
      } catch (error) {
        console.error('Failed to load alignment data:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load alignment');
        setReport(null);
        setObjectiveTree([]);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [userId, currentWorkspaceId, fetchItems, itemCount]);
  
  const header = (
    <PageHeader
      icon={<Target className="h-4 w-4 text-consulting-slate-blue-600" />}
      iconBg="bg-consulting-slate-blue-50"
      title="Strategy Alignment"
      subtitle="Inspect how priorities map to strategic objectives"
      actions={
        <div className="flex items-center gap-2">
          <StrategyConnectivityHelpButton context="alignment" />
          <button onClick={() => setShowAddObjectiveModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors text-sm font-medium">
            <Plus className="h-4 w-4" />
            Add Objective
          </button>
        </div>
      }
    />
  );

  const moduleChooser = (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] p-4">
      <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-3">Choose a strategy approach</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Classic Strategy */}
        <div className="border border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-3 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[var(--text-ter)] shrink-0" />
            <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Classic Strategy</span>
            <span className="ml-auto inline-flex items-center rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]">Current page</span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Vision · Mission · North Star · Strategic Drivers. Works well for teams that want a structured goal-setting framework.</p>
          <div className="mt-2 flex gap-2">
            <Link
              to="/prioritization/strategy/list"
              className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] border border-[var(--slate)] dark:border-[var(--slate)] px-2.5 py-1 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
            >
              View strategies
            </Link>
          </div>
        </div>

        {/* Opportunity Solution Tree */}
        <div className="border border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-3 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-[var(--text-ter)] shrink-0" />
            <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Opportunity Solution Tree</span>
            <span className="ml-auto inline-flex items-center rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]">Torres</span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Desired Outcome → Opportunities → Solutions → Experiments. Continuous discovery driven by customer interviews.</p>
          <div className="mt-2 flex gap-2">
            <Link
              to="/prioritization/strategy/new?framework=ost"
              className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] border border-[var(--slate)] dark:border-[var(--slate)] px-2.5 py-1 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
            >
              New tree
            </Link>
          </div>
        </div>

        {/* Kernel Strategy — hidden when module is disabled */}
        {kernelEnabled && <div className="border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-3 flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-[var(--mustard)] shrink-0" />
            <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Kernel Strategy</span>
            <span className="ml-auto inline-flex items-center rounded-full bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 px-2 py-0.5 text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">Rumelt</span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Diagnosis · Guiding Policy · Coherent Actions. Strategy as problem-solving — find the Crux, build proximate objectives, capture team Takes.</p>
          <div className="mt-2 flex gap-2">
            <Link
              to="/prioritization/strategy/kernel"
              className="text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)] hover:text-[var(--mustard)] border border-[var(--mustard)] dark:border-[var(--mustard)] px-2.5 py-1 hover:bg-[var(--mustard-dim)] dark:hover:bg-[var(--mustard)]/20 transition-colors"
            >
              View kernel strategies
            </Link>
            <Link
              to="/prioritization/strategy/kernel/new"
              className="text-xs font-medium text-white bg-[var(--mustard)] hover:bg-[var(--mustard)] px-2.5 py-1 transition-colors"
            >
              New kernel strategy
            </Link>
          </div>
        </div>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {header}
        {moduleChooser}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-consulting-slate-blue-600" />
        </div>
      </div>
    );
  }

  if (!currentWorkspaceId) {
    return (
      <div className="space-y-6">
        {header}
        {moduleChooser}
        <div className="p-8 text-center text-[var(--text-ter)] border border-[var(--rule)] bg-[var(--surface)]">
          Select a workspace to view alignment.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6">
        {header}
        {moduleChooser}
        <div className="p-8 text-center border border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]">
          <AlertTriangle className="h-12 w-12 mx-auto text-[var(--mustard)] mb-3" />
          <p className="font-medium">Could not load alignment</p>
          <p className="text-sm mt-1">{loadError}</p>
          <p className="text-sm mt-2 text-[var(--mustard)]">Check that you're signed in and the workspace is selected, then refresh.</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="space-y-6">
        {header}
        {moduleChooser}
        <div className="p-8 text-center text-[var(--text-ter)] border border-[var(--rule)] bg-[var(--surface)]">
          No alignment data yet. Add objectives and map priorities to get started.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      {moduleChooser}
      
            {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[var(--hover-tint)] dark:bg-[var(--surface)] w-fit mb-6 border dark:border-[var(--rule)]">
        {(['overview', 'objectives', 'gaps'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2  text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm dark:shadow-none dark:border dark:border-[var(--rule-bright)]'
                : 'text-[var(--text-sec)] dark:text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'gaps' && report.gaps.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-[var(--coral-dim)] dark:bg-[var(--coral)]/30 text-[var(--coral)] dark:text-[var(--coral)] rounded-full">
                {report.gaps.length}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-3 gap-6">
          {/* Score Card */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
            <h3 className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">Alignment Score</h3>
            <div className="flex justify-center">
              <AlignmentScoreRing score={report.alignmentScore} />
            </div>
            <p className="text-center text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-4">
              {report.alignmentScore >= 80 ? 'Strong alignment with strategy' :
               report.alignmentScore >= 60 ? 'Moderate alignment - review gaps' :
               'Weak alignment - action needed'}
            </p>
          </div>
          
          {/* Stats */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
            <h3 className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">Coverage</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">Objectives with priorities</span>
                  <span className="font-medium dark:text-[var(--text-pri)]">{report.mappedObjectives}/{report.totalObjectives}</span>
                </div>
                <div className="h-2 bg-[var(--hover-tint)] dark:bg-[var(--rule-bright)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-consulting-slate-blue-500 rounded-full"
                    style={{ width: `${(report.mappedObjectives / Math.max(report.totalObjectives, 1)) * 100}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">Priorities with objectives</span>
                  <span className="font-medium dark:text-[var(--text-pri)]">{report.mappedPriorities}/{report.totalPriorities}</span>
                </div>
                <div className="h-2 bg-[var(--hover-tint)] dark:bg-[var(--rule-bright)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--green)] rounded-full"
                    style={{ width: `${(report.mappedPriorities / Math.max(report.totalPriorities, 1)) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Gaps */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">Top Gaps</h3>
              {report.gaps.length > 3 && (
                <button
                  onClick={() => setActiveTab('gaps')}
                  className="text-xs text-consulting-slate-blue-600 dark:text-[var(--text-ter)] hover:text-consulting-slate-blue-700"
                >
                  View all
                </button>
              )}
            </div>
            {report.gaps.length === 0 ? (
              <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)]">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">No alignment gaps detected</span>
              </div>
            ) : (
              <div className="space-y-2">
                {report.gaps.slice(0, 3).map((gap, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <AlertTriangle className={`h-4 w-4 ${
                      gap.severity === 'high' ? 'text-[var(--coral)]' :
                      gap.severity === 'medium' ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'
                    }`} />
                    <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate">{gap.entityName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Objectives Tab */}
      {activeTab === 'objectives' && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            <h3 className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Strategic Objective Hierarchy</h3>
          </div>
          
          {objectiveTree.length === 0 ? (
            <p className="text-[var(--text-ter)] text-center py-8">
              No strategic objectives defined yet
            </p>
          ) : (
            <div className="divide-y divide-[var(--rule)]">
              {objectiveTree.map(node => (
                <ObjectiveTreeNode key={node.objective.id} node={node} />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Gaps Tab */}
      {activeTab === 'gaps' && (
        <div className="space-y-4">
          {report.gaps.length === 0 ? (
            <div className="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]/40 p-8 text-center">
              <CheckCircle className="h-12 w-12 text-[var(--green)] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[var(--green)] dark:text-[var(--text-ter)]">Perfect Alignment!</h3>
              <p className="text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
                All priorities are mapped to strategic objectives
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {report.gaps.length} alignment gap{report.gaps.length !== 1 ? 's' : ''} detected.
                Address these to improve strategy execution.
              </p>
              {report.gaps.map((gap, i) => (
                <GapCard key={i} gap={gap} />
              ))}
            </>
          )}
        </div>
      )}
      
      {/* Report Metadata */}
      <div className="mt-8 pt-4 border-t border-[var(--rule)] dark:border-[var(--rule)] text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] text-center">
        Report generated {new Date(report.generatedAt).toLocaleString()}
      </div>

      {/* Add Objective Modal */}
      {showAddObjectiveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={() => setShowAddObjectiveModal(false)} />
            <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-[var(--text-pri)]">Add Strategic Objective</h3>
                <button onClick={() => setShowAddObjectiveModal(false)} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)]">Name</label>
                  <input
                    type="text"
                    value={newObjectiveName}
                    onChange={(e) => setNewObjectiveName(e.target.value)}
                    className="mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
                    placeholder="e.g., Increase ARR by 40%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)]">Description</label>
                  <textarea
                    value={newObjectiveDescription}
                    onChange={(e) => setNewObjectiveDescription(e.target.value)}
                    rows={2}
                    className="mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
                    placeholder="What does this objective aim to achieve?"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)]">Time Horizon</label>
                    <select
                      value={newObjectiveTimeHorizon}
                      onChange={(e) => setNewObjectiveTimeHorizon(e.target.value as 'quarterly' | 'annual' | 'multi-year')}
                      className="bg-[var(--bg-raised)] text-[var(--text-pri)] mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                      <option value="multi-year">Multi-year</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)]">Priority</label>
                    <select
                      value={newObjectivePriority}
                      onChange={(e) => setNewObjectivePriority(e.target.value as 'critical' | 'high' | 'medium' | 'low')}
                      className="bg-[var(--bg-raised)] text-[var(--text-pri)] mt-1 block w-full border-[var(--rule)] shadow-sm focus:border-[var(--slate)] focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddObjectiveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (newObjectiveName.trim() && userId && currentWorkspaceId) {
                      await createObjective({
                        name: newObjectiveName.trim(),
                        description: newObjectiveDescription.trim(),
                        timeHorizon: newObjectiveTimeHorizon,
                        priority: newObjectivePriority,
                        status: 'active',
                        workspaceId: currentWorkspaceId,
                        createdBy: userId,
                      }, userId);
                      // Reset form
                      setNewObjectiveName('');
                      setNewObjectiveDescription('');
                      setNewObjectiveTimeHorizon('quarterly');
                      setNewObjectivePriority('high');
                      setShowAddObjectiveModal(false);
                      // Reload data with real items
                      const { priorityIds, priorityNames } = getActiveItemArgs();
                      const newReport = await generateAlignmentReport(currentWorkspaceId, priorityIds, priorityNames);
                      const tree = await getObjectiveTree(currentWorkspaceId);
                      setReport(newReport);
                      setObjectiveTree(tree);
                    }
                  }}
                  disabled={!newObjectiveName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] border border-transparent hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Objective
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


